import { useContext, useState, useEffect } from "react";
import { TransferContext } from "../../context/TransferContext";
import confirmTransferDetailsValidation from "../../utils/confirmTransferDetailsValidation";
import UserDetails, { Address } from "../../class/userDetails";
import {
  STEP_CONFIRM_TRANSFER_DETAILS,
  STEP_INITIATE_TRANSFER,
  STEP_IRA_OPEN_FORM,
  STEP_MAILING_ADDRESS,
  STEP_MARTIAL_INFO,
  STEP_OPEN_IRA,
  STEP_PERSONAL_INFO,
  STEP_POST_TRANSFER,
} from "../../class/transferDetails";
import Analytics from "../../utils/productAnalytics";
import { ModalContainerContentHeader, ModalContainerCTAButtonDiv } from "../general/modal/styles";
import { BodyLargeRegular, TitleLarge } from "../../components/font";
import { Button } from "@manifesthhqinc/ui-library";
import onLoadAddress from "../../api/APIAddress";

export type Validation = {
  [key in keyof UserDetails | keyof Address | "phoneNumber" | "signature" | "terms"]?: string | undefined;
};

export enum ConfirmTransferStep {
  PERSONAL_DETAILS,
  MAILING_ADDRESS,
  SPOUSAL_INFO,
  INITIATE_TRANSFER,
}

export const steps: Array<ConfirmTransferStep> = [
  ConfirmTransferStep.PERSONAL_DETAILS,
  ConfirmTransferStep.MAILING_ADDRESS,
  ConfirmTransferStep.INITIATE_TRANSFER,
];

export type ConfirmTransferScreenProps = ReturnType<typeof useConfirmTransfer>;

const useConfirmTransfer = () => {
  const [signatureDataURL, setSignatureDataURL] = useState<string | undefined>(undefined);
  const [hasTermsAgreed, setHasTermsAgreed] = useState<boolean>(false);
  const {
    userDetails,
    updateUserDetails,
    transferDetails,
    goToScreen,
    updateUserDetailsToAPI,
    updateUserPhoneNumberToAPI,
    uploadSignatureToAPI,
    initiateTransferToAPI,
    suffixList,
    citizenshipList,
    statesList,
    isLoading,
    setIsLoading,
    setShowError,
    currentScreen,
    userPhoneNumber,
    setUserPhoneNumber,
    validatePhoneNumber,
    validatedPhoneNumber,
    setModalContent,
    setShowModal,
    updateIRAOpenFormData,
    updateFuturemoneyConsent,
  } = useContext(TransferContext);
  const isFutureMoney =
    transferDetails.destinationAccount?.providerName === "Futuremoney" &&
    transferDetails.destinationAccount.yetToOpen === true;

  const [shouldContinue, setShouldContinue] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>();
  const [isInitiatingTransfer, setIsInitiatingTransfer] = useState<boolean>(false);
  const [currentConfirmTransferStep, setCurrentConfirmTransferStep] = useState<ConfirmTransferStep>(steps[0]);
  const [iraFormData, setIRAFormData] = useState<Record<string, string>>({});
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [mailingAddress, setMailingAddress] = useState<Address>(userDetails.address);

  const updateAutofillAddress = (addObj: Record<string, string>) => {
    setMailingAddress({
      ...mailingAddress,
      addressLine1: addObj.addressLabel,
      city: addObj.city,
      state: addObj.state,
      zipCode: addObj.postalCode,
    });
  };

  useEffect(() => {
    updateUserDetails({ address: mailingAddress });
  }, [mailingAddress]);

  useEffect(() => {
    setShouldContinue(false);
    if (currentScreen === STEP_PERSONAL_INFO) {
      setShouldContinue(
        confirmTransferDetailsValidation.validatePersonalDetails(userDetails).allPassed &&
          validatedPhoneNumber === undefined &&
          userPhoneNumber !== "",
      );
    } else if (currentScreen === STEP_MARTIAL_INFO) {
      setShouldContinue(confirmTransferDetailsValidation.validateSpouseInfo(userDetails).allPassed);
    } else if (currentScreen === STEP_MAILING_ADDRESS) {
      setShouldContinue(confirmTransferDetailsValidation.validateMailingAddress(userDetails).allPassed);
    } else {
      // INITIATE_TRANSFER
      setShouldContinue(userDetails.signatureFileName !== "" && hasTermsAgreed);
    }
  }, [currentScreen, userDetails, validatedPhoneNumber, userPhoneNumber]);

  const initiateTransfer = async (url: string) => {
    try {
      setIsInitiatingTransfer(true);
      // upload signature
      await uploadSignatureToAPI(url);
      const status = await initiateTransferToAPI();
      setIsInitiatingTransfer(false);
      if (status === "PENDING") {
        goToScreen(STEP_OPEN_IRA);
      } else {
        goToScreen(STEP_POST_TRANSFER);
      }
    } catch (e: any) {
      // TODO: do nothing
      // Errors are caught in the above APIs
    }
  };

  useEffect(() => {
    setValidation({});
    validatePhoneNumber();
  }, [userDetails, userPhoneNumber, hasTermsAgreed, signatureDataURL]);

  const validateFormDetails = () => {
    let isValidated = true;
    let formValidation = {};
    if (currentScreen === STEP_PERSONAL_INFO) {
      const personalDetailsValidation = {
        firstName: confirmTransferDetailsValidation.firstName(userDetails),
        lastName: confirmTransferDetailsValidation.lastName(userDetails),
        dateOfBirth: confirmTransferDetailsValidation.dateOfBirth(userDetails),
        citizenship: confirmTransferDetailsValidation.citizenship(userDetails),
        ssnOrTin: confirmTransferDetailsValidation.ssnOrTin(userDetails),
        maritalStatus: confirmTransferDetailsValidation.martialStatus(userDetails),
        phoneNumber: validatePhoneNumber() ? "" : "Please enter a valid phone number",
      };
      formValidation = personalDetailsValidation;
      isValidated = Object.values(personalDetailsValidation).filter((e) => e).length === 0;
    }
    if (currentScreen === STEP_MARTIAL_INFO) {
      const maritalStatusValidation = {
        spouseName: confirmTransferDetailsValidation.spouseName(userDetails),
        spouseEmail: confirmTransferDetailsValidation.spouseEmail(userDetails),
        spousePhoneNumber: confirmTransferDetailsValidation.spousePhoneNumber(userDetails),
        spouseDateOfBirth: confirmTransferDetailsValidation.spouseDateOfBirth(userDetails),
      };
      isValidated = Object.values(maritalStatusValidation).filter((e) => e).length === 0;
      formValidation = maritalStatusValidation;
    }
    if (currentScreen === STEP_MAILING_ADDRESS) {
      const addressValidation = {
        addressLine1: confirmTransferDetailsValidation.addressLine1(userDetails),
        city: confirmTransferDetailsValidation.city(userDetails),
        state: confirmTransferDetailsValidation.state(userDetails),
        zipCode: confirmTransferDetailsValidation.zipCode(userDetails),
      };
      isValidated = Object.values(addressValidation).filter((e) => e).length === 0;
      formValidation = addressValidation;
    }
    if (currentScreen === STEP_INITIATE_TRANSFER) {
      isValidated = !!signatureDataURL && hasTermsAgreed;
      formValidation = {
        signature: !signatureDataURL ? "Please sign to authorize your transfer" : "",
        terms: !hasTermsAgreed ? "Please read and agree the terms above" : "",
      };
    }

    return { isValidated, formValidation };
  };

  const onClickContinue = async () => {
    const { isValidated, formValidation } = validateFormDetails();
    if (!isValidated) {
      setValidation(formValidation);
      return;
    }
    if (currentScreen === STEP_CONFIRM_TRANSFER_DETAILS) {
      Analytics.track({
        name: "Clicked Continue on Confirm transfer details screen",
      });
      goToScreen(STEP_PERSONAL_INFO);
    } else if (currentScreen === STEP_INITIATE_TRANSFER) {
      if (!signatureDataURL || !hasTermsAgreed) {
        return;
      }
      Analytics.track({
        name: "Initiated transfer",
        properties: {
          transferId: transferDetails.transferId,
        },
      });
      if (isFutureMoney) {
        updateFuturemoneyConsent();
      }
      initiateTransfer(signatureDataURL);
    } else if (currentScreen === STEP_IRA_OPEN_FORM) {
      // do something
      // send data to API open IRA data
      setIsButtonLoading(true);
      await updateIRAOpenFormData(iraFormData);
      setIsButtonLoading(false);
      goToScreen(STEP_INITIATE_TRANSFER);
    } else {
      await updateUserDetailsToAPI();
      if (currentScreen === STEP_PERSONAL_INFO) {
        await updateUserPhoneNumberToAPI(userPhoneNumber);
      }
      if (currentScreen === STEP_PERSONAL_INFO && userDetails.maritalStatus.toLowerCase() === "married") {
        Analytics.track({
          name: "Clicked Continue on Personal details screen",
        });
        goToScreen(STEP_MARTIAL_INFO);
      } else if (currentScreen === STEP_PERSONAL_INFO && userDetails.maritalStatus.toLowerCase() === "not married") {
        Analytics.track({
          name: "Clicked Continue on Personal details screen",
        });
        goToScreen(STEP_MAILING_ADDRESS);
      } else if (currentScreen === STEP_MARTIAL_INFO) {
        Analytics.track({ name: "Clicked Continue on Spousal info screen" });
        setCurrentConfirmTransferStep(ConfirmTransferStep.MAILING_ADDRESS);
        goToScreen(STEP_MAILING_ADDRESS);
      } else if (currentScreen === STEP_MAILING_ADDRESS) {
        Analytics.track({
          name: "Clicked Continue on Address screen",
        });
        if (userDetails.address.addressLine2) {
          onClickConfirmAddress();
        } else {
          setShowModal(true);
          setModalContent(
            <>
              <ModalContainerContentHeader>
                <TitleLarge>No apartment or suite number?</TitleLarge>
                <BodyLargeRegular>
                  Please confirm that your address does not include an apartment, unit or suite.
                </BodyLargeRegular>
              </ModalContainerContentHeader>
              <ModalContainerCTAButtonDiv>
                <Button onClick={() => setShowModal(false)} variant="secondary">
                  Edit
                </Button>
                <Button onClick={onClickConfirmAddress} name="continueWithNoUnit">
                  Continue
                </Button>
              </ModalContainerCTAButtonDiv>
            </>,
          );
        }
      }
    }
  };

  const onClickConfirmAddress = () => {
    setShowModal(false);
    setModalContent(<></>);
    if (isFutureMoney) {
      goToScreen(STEP_IRA_OPEN_FORM);
    } else {
      goToScreen(STEP_INITIATE_TRANSFER);
    }
  };

  const validateField = (key: keyof UserDetails | keyof Address) => {
    const error = confirmTransferDetailsValidation.validateField(key, userDetails);
    setValidation({
      ...validation,
      [key]: error,
    });
  };

  const toggleTermsAgreement = () => setHasTermsAgreed(!hasTermsAgreed);

  return {
    userDetails,
    onClickContinue,
    currentConfirmTransferStep,
    updateUserDetails,
    transferDetails,
    suffixList,
    citizenshipList,
    statesList,
    shouldContinue,
    validation,
    validateField,
    initiateTransfer,
    isLoading,
    setIsLoading,
    signatureDataURL,
    setSignatureDataURL,
    hasTermsAgreed,
    setHasTermsAgreed,
    toggleTermsAgreement,
    setShowError,
    setIsInitiatingTransfer,
    isInitiatingTransfer,
    currentScreen,
    userPhoneNumber,
    setUserPhoneNumber,
    validatePhoneNumber,
    validatedPhoneNumber,
    isFutureMoney,
    iraFormData,
    setIRAFormData,
    isButtonLoading,
    onLoadAddress,
    mailingAddress,
    setMailingAddress,
    updateAutofillAddress,
  };
};

export default useConfirmTransfer;
